import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getInvoicesByFileOpening } from '../services/invoices';
import toast from 'react-hot-toast';
import { deleteInvoice } from '../services/invoices';
import ConfirmationModal from '../components/ConfirmationModal';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const InvoicesList = () => {
    const { id } = useParams();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInvoices = async () => {
            if (!id) return;

            try {
                const offset = (currentPage - 1) * limit;
                const { invoices, count } = await getInvoicesByFileOpening(id, {
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    searchTerm
                });
                setInvoices(invoices);
                setTotal(count);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchInvoices();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [id, limit, currentPage, sortBy, sortOrder, searchTerm]);

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSortByChange = (e) => {
        const [newSortBy, newSortOrder] = e.target.value.split(':');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const openModal = (invoiceId) => {
        setSelectedInvoiceId(invoiceId);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedInvoiceId(null);
        setIsModalOpen(false);
    };

    const handleDelete = async () => {
        if (selectedInvoiceId) {
            try {
                await deleteInvoice(selectedInvoiceId);
                toast.success('Invoice deleted successfully!');
                // Refetch the data to update the list
                const offset = (currentPage - 1) * limit;
                const { invoices, count } = await getInvoicesByFileOpening(id, {
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    searchTerm
                });
                setInvoices(invoices);
                setTotal(count);
            } catch (error) {
                toast.error(error.message);
            }
            closeModal();
        }
    };

    return (
        <DashboardLayout pageTitle={`Invoices - File Opening #${id}`}>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                    <input
                        type="text"
                        placeholder="Search invoices..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    />
                    <select
                        value={limit}
                        onChange={handleLimitChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <select
                        value={`${sortBy}_${sortOrder}`}
                        onChange={handleSortByChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value="id:desc">ID (Z-A)</option>
                        <option value="id:asc">ID (A-Z)</option>
                        <option value="date:desc">Date (Newest First)</option>
                        <option value="date:asc">Date (Oldest First)</option>
                        <option value="grand_total:desc">Amount (Highest First)</option>
                        <option value="grand_total:asc">Amount (Lowest First)</option>
                    </select>
                </div>
                <div>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                    >
                        Back
                    </button>
                    <Link
                        to={`/file-opening/${id}/invoice/create`}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow"
                    >
                        New Invoice
                    </Link>
                </div>
            </div>
            <TableContainer>
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <>
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Invoice #</th>
                                    <th scope="col" className="px-6 py-3">Amount</th>
                                    <th scope="col" className="px-6 py-3">Date</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.length > 0 ? (
                                    invoices.map((invoice) => (
                                        <tr key={invoice.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4">{invoice.id}</td>
                                            <td className="px-6 py-4">£{invoice.grand_total?.toFixed(2)}</td>
                                            <td className="px-6 py-4">{invoice.date}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs ${invoice.status === 'paid'
                                                    ? 'bg-green-100 text-green-800'
                                                    : invoice.status === 'pending'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {invoice.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Link
                                                    to={`/file-opening/${id}/invoice/${invoice.id}`}
                                                    className="text-blue-600 hover:underline mr-4"
                                                >
                                                    View
                                                </Link>
                                                <Link
                                                    to={`/file-opening/${id}/invoice/${invoice.id}/edit`}
                                                    className="text-blue-600 hover:underline mr-4"
                                                >
                                                    Edit
                                                </Link>
                                                <button
                                                    onClick={() => openModal(invoice.id)}
                                                    className="text-red-600 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                            No invoices found for this file opening.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination
                            currentPage={currentPage}
                            total={total}
                            limit={limit}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </TableContainer>
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this invoice?"
            />
        </DashboardLayout>
    );
};

export default InvoicesList;